import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faEye,
  faEyeSlash,
  faShieldAlt,
  faFilter,
  faDownload,
  faChevronDown,
  faChevronRight,
  faCrosshairs,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../contexts/ThemeContext';
import riskScoringService from '../../services/riskScoringService';

// Detection maturity levels (DeTTECT-inspired)
const DETECTION_LEVELS = {
  0: { name: 'None', color: 'red', description: 'No detection capability' },
  1: { name: 'Basic', color: 'orange', description: 'Basic/signature-based detection' },
  2: { name: 'Fair', color: 'yellow', description: 'Rule-based detection with some behavioral' },
  3: { name: 'Good', color: 'blue', description: 'Behavioral detection with good coverage' },
  4: { name: 'Very Good', color: 'green', description: 'Advanced detection with correlation' },
  5: { name: 'Excellent', color: 'emerald', description: 'Comprehensive detection with threat hunting' }
};

// Visibility levels
const VISIBILITY_LEVELS = {
  0: { name: 'None', color: 'red', description: 'No visibility into technique execution' },
  1: { name: 'Minimal', color: 'orange', description: 'Limited logging, may miss events' },
  2: { name: 'Basic', color: 'yellow', description: 'Basic event logging enabled' },
  3: { name: 'Good', color: 'blue', description: 'Good event collection and retention' },
  4: { name: 'Excellent', color: 'green', description: 'Comprehensive logging and correlation' }
};

const EnhancedGapAnalysis = ({
  actions = [],
  findings = [],
  detectionData = null,
  onExport
}) => {
  const { darkMode } = useTheme();
  const [expandedTactic, setExpandedTactic] = useState(null);
  const [filterPriority, setFilterPriority] = useState('all');
  const [showOnlyGaps, setShowOnlyGaps] = useState(false);
  const [sortBy, setSortBy] = useState('priority'); // priority, technique, detection

  // Analyze techniques and calculate gap scores
  const gapAnalysis = useMemo(() => {
    const techniqueMap = new Map();

    // Process all actions
    actions.forEach(action => {
      if (!action.mitreAttackTechnique?.id) return;

      const techId = action.mitreAttackTechnique.id;
      if (!techniqueMap.has(techId)) {
        techniqueMap.set(techId, {
          id: techId,
          name: action.mitreAttackTechnique.name,
          tactic: action.mitreAttackTechnique.tactic,
          attempts: 0,
          successful: 0,
          detected: 0,
          blocked: 0,
          partial: 0,
          failed: 0,
          findings: [],
          isCritical: riskScoringService.criticalTechniques.includes(techId),
          isHighRisk: riskScoringService.highRiskTechniques.includes(techId)
        });
      }

      const entry = techniqueMap.get(techId);
      entry.attempts++;
      if (action.result === 'successful') entry.successful++;
      else if (action.result === 'detected') entry.detected++;
      else if (action.result === 'blocked') entry.blocked++;
      else if (action.result === 'partial') entry.partial++;
      else if (action.result === 'failed') entry.failed++;
    });

    // Add detection data if available
    if (detectionData?.techniqueDetection) {
      detectionData.techniqueDetection.forEach(tech => {
        if (techniqueMap.has(tech.id)) {
          const entry = techniqueMap.get(tech.id);
          entry.detectionRate = parseFloat(tech.detectionRate);
          entry.correlationRate = parseFloat(tech.correlationRate);
        }
      });
    }

    // Calculate gap scores for each technique
    const analyzedTechniques = Array.from(techniqueMap.values()).map(tech => {
      // Detection score (0-5)
      let detectionLevel = 0;
      const detectionRate = tech.detectionRate ||
        (tech.attempts > 0 ? ((tech.detected + tech.blocked) / tech.attempts) * 100 : 0);

      if (detectionRate >= 95) detectionLevel = 5;
      else if (detectionRate >= 80) detectionLevel = 4;
      else if (detectionRate >= 60) detectionLevel = 3;
      else if (detectionRate >= 40) detectionLevel = 2;
      else if (detectionRate >= 20) detectionLevel = 1;

      // Visibility score (based on whether we got findings/logs)
      let visibilityLevel = 0;
      if (tech.blocked > 0) visibilityLevel = 4;
      else if (tech.detected > 0) visibilityLevel = 3;
      else if (tech.partial > 0) visibilityLevel = 2;
      else if (tech.failed > 0) visibilityLevel = 1;

      // Gap priority calculation
      let gapPriority = 'low';
      let gapScore = 0;

      // Critical/high-risk techniques with low detection are critical gaps
      if (tech.isCritical && detectionLevel < 3) {
        gapPriority = 'critical';
        gapScore = 100 - (detectionLevel * 20);
      } else if (tech.isHighRisk && detectionLevel < 3) {
        gapPriority = 'high';
        gapScore = 80 - (detectionLevel * 15);
      } else if (detectionLevel < 2) {
        gapPriority = 'medium';
        gapScore = 60 - (detectionLevel * 10);
      } else if (detectionLevel < 3) {
        gapPriority = 'low';
        gapScore = 40 - (detectionLevel * 8);
      }

      // Adjust based on successful attacks
      if (tech.successful > 0 && detectionLevel < 4) {
        gapScore += tech.successful * 5;
        if (gapPriority === 'low') gapPriority = 'medium';
        if (gapPriority === 'medium' && tech.successful > 2) gapPriority = 'high';
      }

      // Generate recommendations
      const recommendations = [];

      if (detectionLevel < 2) {
        recommendations.push({
          type: 'detection',
          message: `Implement detection rules for ${tech.id} - ${tech.name}`,
          priority: 'high'
        });
      }

      if (visibilityLevel < 2) {
        recommendations.push({
          type: 'visibility',
          message: `Enable logging for ${tech.tactic} activity`,
          priority: 'high'
        });
      }

      if (tech.successful > 0 && tech.blocked === 0) {
        recommendations.push({
          type: 'prevention',
          message: `Consider preventive controls to block ${tech.id}`,
          priority: 'medium'
        });
      }

      if (tech.detected > 0 && tech.blocked === 0) {
        recommendations.push({
          type: 'response',
          message: `Add automated response for ${tech.id} detections`,
          priority: 'low'
        });
      }

      return {
        ...tech,
        detectionLevel,
        visibilityLevel,
        detectionRate,
        gapPriority,
        gapScore,
        recommendations
      };
    });

    // Group by tactic
    const byTactic = {};
    analyzedTechniques.forEach(tech => {
      const tactic = tech.tactic || 'Unknown';
      if (!byTactic[tactic]) {
        byTactic[tactic] = {
          name: tactic,
          techniques: [],
          avgDetection: 0,
          avgVisibility: 0,
          criticalGaps: 0,
          highGaps: 0
        };
      }
      byTactic[tactic].techniques.push(tech);
    });

    // Calculate tactic-level stats
    Object.values(byTactic).forEach(tactic => {
      const techs = tactic.techniques;
      tactic.avgDetection = techs.reduce((sum, t) => sum + t.detectionLevel, 0) / techs.length;
      tactic.avgVisibility = techs.reduce((sum, t) => sum + t.visibilityLevel, 0) / techs.length;
      tactic.criticalGaps = techs.filter(t => t.gapPriority === 'critical').length;
      tactic.highGaps = techs.filter(t => t.gapPriority === 'high').length;
    });

    // Calculate overall scores
    const totalTechniques = analyzedTechniques.length;
    const avgDetection = totalTechniques > 0
      ? analyzedTechniques.reduce((sum, t) => sum + t.detectionLevel, 0) / totalTechniques
      : 0;
    const avgVisibility = totalTechniques > 0
      ? analyzedTechniques.reduce((sum, t) => sum + t.visibilityLevel, 0) / totalTechniques
      : 0;

    const criticalGaps = analyzedTechniques.filter(t => t.gapPriority === 'critical');
    const highGaps = analyzedTechniques.filter(t => t.gapPriority === 'high');
    const mediumGaps = analyzedTechniques.filter(t => t.gapPriority === 'medium');

    return {
      techniques: analyzedTechniques,
      byTactic,
      summary: {
        totalTechniques,
        avgDetection: Math.round(avgDetection * 10) / 10,
        avgVisibility: Math.round(avgVisibility * 10) / 10,
        criticalGaps: criticalGaps.length,
        highGaps: highGaps.length,
        mediumGaps: mediumGaps.length,
        overallScore: Math.round((avgDetection / 5) * 100)
      }
    };
  }, [actions, findings, detectionData]);

  // Filter and sort techniques
  const filteredTechniques = useMemo(() => {
    let filtered = [...gapAnalysis.techniques];

    if (filterPriority !== 'all') {
      filtered = filtered.filter(t => t.gapPriority === filterPriority);
    }

    if (showOnlyGaps) {
      filtered = filtered.filter(t => t.detectionLevel < 3);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
          return priorityOrder[a.gapPriority] - priorityOrder[b.gapPriority];
        case 'technique':
          return a.id.localeCompare(b.id);
        case 'detection':
          return a.detectionLevel - b.detectionLevel;
        default:
          return b.gapScore - a.gapScore;
      }
    });

    return filtered;
  }, [gapAnalysis.techniques, filterPriority, showOnlyGaps, sortBy]);

  // Get level color
  const getLevelColor = (level, type = 'detection') => {
    const levels = type === 'detection' ? DETECTION_LEVELS : VISIBILITY_LEVELS;
    const levelInfo = levels[level] || levels[0];
    const colorMap = {
      red: darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800',
      orange: darkMode ? 'bg-orange-900 text-orange-200' : 'bg-orange-100 text-orange-800',
      yellow: darkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800',
      blue: darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800',
      green: darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800',
      emerald: darkMode ? 'bg-emerald-900 text-emerald-200' : 'bg-emerald-100 text-emerald-800'
    };
    return colorMap[levelInfo.color] || colorMap.red;
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const badges = {
      critical: darkMode ? 'bg-red-600 text-white' : 'bg-red-500 text-white',
      high: darkMode ? 'bg-orange-600 text-white' : 'bg-orange-500 text-white',
      medium: darkMode ? 'bg-yellow-600 text-white' : 'bg-yellow-500 text-white',
      low: darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
    };
    return badges[priority] || badges.low;
  };

  // Export gap analysis
  const exportAnalysis = () => {
    const data = {
      summary: gapAnalysis.summary,
      techniques: gapAnalysis.techniques.map(t => ({
        id: t.id,
        name: t.name,
        tactic: t.tactic,
        detectionLevel: t.detectionLevel,
        visibilityLevel: t.visibilityLevel,
        detectionRate: t.detectionRate,
        gapPriority: t.gapPriority,
        gapScore: t.gapScore,
        attempts: t.attempts,
        successful: t.successful,
        detected: t.detected,
        blocked: t.blocked,
        recommendations: t.recommendations
      }))
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gap-analysis-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Header */}
      <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Detection & Visibility Gap Analysis
            </h3>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              DeTTECT-inspired analysis of {gapAnalysis.summary.totalTechniques} techniques
            </p>
          </div>
          <button
            onClick={exportAnalysis}
            className={`px-3 py-1 rounded ${
              darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            Export
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-3 mb-4">
          <div className={`p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Overall Score
            </div>
            <div className={`text-2xl font-bold ${
              gapAnalysis.summary.overallScore >= 70
                ? 'text-green-500'
                : gapAnalysis.summary.overallScore >= 40
                ? 'text-yellow-500'
                : 'text-red-500'
            }`}>
              {gapAnalysis.summary.overallScore}%
            </div>
          </div>
          <div className={`p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Avg Detection
            </div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {gapAnalysis.summary.avgDetection}/5
            </div>
          </div>
          <div className={`p-3 rounded ${darkMode ? 'bg-red-900/50' : 'bg-red-100'}`}>
            <div className={`text-xs ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
              Critical Gaps
            </div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-red-200' : 'text-red-700'}`}>
              {gapAnalysis.summary.criticalGaps}
            </div>
          </div>
          <div className={`p-3 rounded ${darkMode ? 'bg-orange-900/50' : 'bg-orange-100'}`}>
            <div className={`text-xs ${darkMode ? 'text-orange-300' : 'text-orange-600'}`}>
              High Gaps
            </div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-orange-200' : 'text-orange-700'}`}>
              {gapAnalysis.summary.highGaps}
            </div>
          </div>
          <div className={`p-3 rounded ${darkMode ? 'bg-yellow-900/50' : 'bg-yellow-100'}`}>
            <div className={`text-xs ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>
              Medium Gaps
            </div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-yellow-200' : 'text-yellow-700'}`}>
              {gapAnalysis.summary.mediumGaps}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-3">
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className={`px-3 py-1 text-sm rounded border ${
              darkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-700'
            }`}
          >
            <option value="all">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={`px-3 py-1 text-sm rounded border ${
              darkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-700'
            }`}
          >
            <option value="priority">Sort by Priority</option>
            <option value="technique">Sort by Technique</option>
            <option value="detection">Sort by Detection Level</option>
          </select>
          <label className={`flex items-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <input
              type="checkbox"
              checked={showOnlyGaps}
              onChange={(e) => setShowOnlyGaps(e.target.checked)}
              className="mr-2 rounded"
            />
            Show only gaps (detection &lt; 3)
          </label>
        </div>
      </div>

      {/* Legend */}
      <div className={`px-4 py-3 border-b ${darkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center space-x-6 text-xs">
          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Detection Levels:</span>
          {Object.entries(DETECTION_LEVELS).map(([level, info]) => (
            <span key={level} className="flex items-center">
              <span className={`w-3 h-3 rounded mr-1 ${getLevelColor(parseInt(level), 'detection')}`}></span>
              {level}: {info.name}
            </span>
          ))}
        </div>
      </div>

      {/* Gap List */}
      <div className="max-h-[500px] overflow-y-auto">
        {filteredTechniques.length === 0 ? (
          <div className="p-8 text-center">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className={`text-5xl mb-4 ${darkMode ? 'text-green-500' : 'text-green-400'}`}
            />
            <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
              {showOnlyGaps ? 'No significant gaps found!' : 'No techniques to analyze.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredTechniques.map(tech => (
              <div key={tech.id} className={`p-4 ${darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className={`px-2 py-0.5 text-xs rounded mr-2 ${getPriorityBadge(tech.gapPriority)}`}>
                        {tech.gapPriority.toUpperCase()}
                      </span>
                      <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {tech.id}
                      </span>
                      {(tech.isCritical || tech.isHighRisk) && (
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className={`ml-2 ${tech.isCritical ? 'text-red-500' : 'text-orange-500'}`}
                          title={tech.isCritical ? 'Critical Technique' : 'High-Risk Technique'}
                        />
                      )}
                    </div>
                    <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {tech.name}
                    </p>
                    <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {tech.tactic} • {tech.attempts} attempts • {tech.successful} successful
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    {/* Detection Level */}
                    <div className="text-center">
                      <div className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Detection
                      </div>
                      <div className={`px-3 py-1 rounded text-sm font-medium ${getLevelColor(tech.detectionLevel, 'detection')}`}>
                        {tech.detectionLevel}/5
                      </div>
                    </div>

                    {/* Visibility Level */}
                    <div className="text-center">
                      <div className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Visibility
                      </div>
                      <div className={`px-3 py-1 rounded text-sm font-medium ${getLevelColor(tech.visibilityLevel, 'visibility')}`}>
                        {tech.visibilityLevel}/4
                      </div>
                    </div>

                    {/* Detection Rate */}
                    <div className="text-center w-20">
                      <div className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Rate
                      </div>
                      <div className={`text-sm font-medium ${
                        tech.detectionRate >= 70
                          ? 'text-green-500'
                          : tech.detectionRate >= 40
                          ? 'text-yellow-500'
                          : 'text-red-500'
                      }`}>
                        {Math.round(tech.detectionRate || 0)}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                {tech.recommendations.length > 0 && (
                  <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Recommendations:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {tech.recommendations.map((rec, idx) => (
                        <span
                          key={idx}
                          className={`px-2 py-1 text-xs rounded ${
                            rec.priority === 'high'
                              ? (darkMode ? 'bg-red-900/50 text-red-200' : 'bg-red-100 text-red-700')
                              : rec.priority === 'medium'
                              ? (darkMode ? 'bg-yellow-900/50 text-yellow-200' : 'bg-yellow-100 text-yellow-700')
                              : (darkMode ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-700')
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={rec.type === 'detection' ? faEye : rec.type === 'visibility' ? faEyeSlash : faShieldAlt}
                            className="mr-1"
                          />
                          {rec.message}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tactic Summary */}
      <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Gap Summary by Tactic
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {Object.values(gapAnalysis.byTactic)
            .sort((a, b) => (b.criticalGaps + b.highGaps) - (a.criticalGaps + a.highGaps))
            .map(tactic => (
              <div
                key={tactic.name}
                className={`p-2 rounded text-center ${
                  tactic.criticalGaps > 0
                    ? (darkMode ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200')
                    : tactic.highGaps > 0
                    ? (darkMode ? 'bg-orange-900/30 border border-orange-700' : 'bg-orange-50 border border-orange-200')
                    : (darkMode ? 'bg-gray-700' : 'bg-gray-100')
                }`}
              >
                <div className={`text-xs font-medium truncate ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {tactic.name}
                </div>
                <div className={`text-lg font-bold ${
                  tactic.avgDetection >= 3 ? 'text-green-500' :
                  tactic.avgDetection >= 2 ? 'text-yellow-500' : 'text-red-500'
                }`}>
                  {Math.round(tactic.avgDetection * 10) / 10}
                </div>
                {(tactic.criticalGaps > 0 || tactic.highGaps > 0) && (
                  <div className="text-xs text-red-500">
                    {tactic.criticalGaps > 0 && `${tactic.criticalGaps} critical`}
                    {tactic.criticalGaps > 0 && tactic.highGaps > 0 && ', '}
                    {tactic.highGaps > 0 && `${tactic.highGaps} high`}
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default EnhancedGapAnalysis;
